import React, { useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Tooltip from '@mui/material/Tooltip';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';
import {
  ranDbFileUploadResponse,
  selectedDeletedSites,
  openDeleteSiteModal,
  selectedSiteForDeletion,
} from '../../common/atom/atoms';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, MenuItem, Menu } from '@mui/material';
import RanDbSiteDetailsModal from './RanDbSiteDetailsModal';

export default function RanDBUploadModalTableDeletedSites() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const newSites = useAtomValue(ranDbFileUploadResponse);
  const data = newSites?.deletedSites;
  const validData = Array?.isArray(data) ? data : [];
  const [selectedRows, setSelectedRows] = useAtom(selectedDeletedSites);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);
  const [selectedSite, setSelectedSite] = React.useState(null);
  const setOpenDeleteModal = useSetAtom(openDeleteSiteModal);
  const setSelectedSiteForDeletion = useSetAtom(selectedSiteForDeletion);

  const handleOpenMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleRowSelectionChange = (updaterOrValue) => {
    if (typeof updaterOrValue === 'function') {
      setSelectedRows(updaterOrValue);
    } else {
      setSelectedRows(updaterOrValue);
    }
  };

  const handleViewSite = () => {
    setDetailsModalOpen(true);
    setSelectedSite(selectedRow);
    handleCloseMenu();
  };

  const handleDeleteSite = () => {
    setSelectedSiteForDeletion(selectedRow);
    setOpenDeleteModal(true);
    handleCloseMenu();
  };

  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'site_code',
        header: 'Site Code',
        size: 120,
      },
      {
        accessorKey: 'physical',
        header: 'Physical',
        size: 120,
      },
      {
        accessorKey: 'region',
        header: 'Region',
        size: 120,
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 120,
      },
      {
        accessorKey: 'coverage_type',
        header: 'Coverage Type',
        size: 120,
      },
      {
        accessorKey: 'site_status',
        header: 'Status',
        size: 120,
      },
      {
        accessorKey: 'usf_site',
        header: 'USF Site',
        size: 120,
      },

      // ✅ 9. Actions column
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <>
            <IconButton onClick={(e) => handleOpenMenu(e, row.original)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ];
  }, []);
  const table = useMaterialReactTable({
    columns,
    data: validData,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: true,
    enableRowSelection: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    onRowSelectionChange: handleRowSelectionChange,
    state: { rowSelection: selectedRows },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    // enableHiding: false,
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 320px)' } },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '26px', // Reduced row height to match main table
        padding: '2px 8px', // Reduced padding to match main table
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip title="Download">
        <svg
          style={{ cursor: 'pointer' }}
          onClick={downloadFile}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7 10L12 15L17 10"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 15V3"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </Tooltip>
    ),
  });

  const downloadFile = () => {
    downloadExcelSheetFunction(validData);
  };

  return (
    <div style={{ marginLeft: '32px', marginRight: '32px', width: '100%' }}>
      <div className="box">
        <MaterialReactTable table={table} />
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
          <MenuItem onClick={handleViewSite}>
            <VisibilityIcon style={{ fontSize: 16, marginRight: 8 }} />
            View
          </MenuItem>
          {/* Edit and Delete buttons commented out for now */}
        </Menu>
      </div>
      <RanDbSiteDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        site={selectedSite}
      />
    </div>
  );
}
